export function filterSupportedWaterfallOptions<T extends {id: string}>(
    options: T[],
    supportedIds: readonly string[],
): T[] {
    const allowed = new Set(
        (supportedIds || []).map((id) => String(id || '').trim()).filter(Boolean),
    );
    return (options || []).filter((o) => allowed.has(String(o.id || '').trim()));
}

/** Fresh copy of every supported platform id — editor filter defaults to all selected. */
export function defaultSelectedPlatformIds(supportedIds: readonly string[]): string[] {
    return (supportedIds || []).map((id) => String(id || '').trim()).filter(Boolean);
}

export type PlatformFilterOption = {id: string; name: string; lineCount: number};

/**
 * Filter dropdown = registry-supported platforms, even when /ad-sources catalog is empty.
 * Catalog names and line counts win when present; unsupported catalog ids are dropped.
 */
export function unionSupportedPlatformOptions(
    options: {id: string; name?: string; lineCount?: number}[],
    supported: {id: string; name: string}[],
): PlatformFilterOption[] {
    const fromCatalog = new Map<string, PlatformFilterOption>();
    for (const o of options || []) {
        const id = String(o?.id || '').trim();
        if (!id) {
            continue;
        }
        fromCatalog.set(id, {
            id,
            name: String(o.name || id),
            lineCount: Number(o.lineCount) || 0,
        });
    }
    const allowed = new Set((supported || []).map((s) => String(s.id || '').trim()).filter(Boolean));
    return (supported || [])
        .map((s) => {
            const id = String(s.id || '').trim();
            if (!id || !allowed.has(id)) {
                return null;
            }
            const hit = fromCatalog.get(id);
            return {
                id,
                name: hit?.name || s.name || id,
                lineCount: hit?.lineCount || 0,
            };
        })
        .filter((o): o is PlatformFilterOption => Boolean(o));
}

export function pickDefaultSupportedWaterfallId(
    options: {id: string}[],
    preferredId: string,
): string {
    const list = options || [];
    const preferred = String(preferredId || '').trim();
    if (preferred && list.some((o) => String(o.id) === preferred)) {
        return preferred;
    }
    return String(list[0]?.id || '');
}

/** After reload/Apply: keep previous selection if still listed, else fallback. */
export function pickWaterfallIdAfterReload(
    options: {id: string}[],
    previousId: string,
    fallbackId: string,
): string {
    const previous = String(previousId || '').trim();
    return pickDefaultSupportedWaterfallId(options, previous || fallbackId);
}
